import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { requirePermission } from '@/lib/route-guards';
import { useHasPermission } from '@/lib/permissions';
import {
  apiErrorCode, CATEGORY_LABELS, draftError, fmtArea, toastApiError, useCreateGeofence, useDeleteGeofence, useUpdateGeofence,
  type GeofenceDraft,
} from '@/lib/geofences';
import { Button } from '@/components/ui/button';
import { QueryError } from '@/components/QueryError';
import { DeleteGeofenceDialog } from '@/components/geofences/DeleteGeofenceDialog';
import { DrawToolbar } from '@/components/geofences/DrawToolbar';
import { GeofenceList } from '@/components/geofences/GeofenceList';
import { GeofenceMap, type DrawMode } from '@/components/geofences/GeofenceMap';
import { GeofenceNameDialog } from '@/components/geofences/GeofenceNameDialog';
import { GeofenceRuleDialog } from '@/components/geofences/GeofenceRuleDialog';
import { GeofenceRulesPanel } from '@/components/geofences/GeofenceRulesPanel';
import type { GeofenceRule } from '@/lib/geofence-rules';

import type { Geofence } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/geofences')({
  beforeLoad: () => requirePermission('geofences.read'),
  component: GeofencesPage,
});

export function GeofencesPage() {
  const query = useQuery({
    queryKey: ['geofences'],
    queryFn: async () => (await api.get<{ data: Geofence[] }>('/geofences')).data,
  });
  const geofences = query.data?.data ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [focusRequest, setFocusRequest] = useState(0);
  const canWrite = useHasPermission('geofences.write');
  const [drawMode, setDrawMode] = useState<DrawMode | null>(null);
  const [draft, setDraft] = useState<GeofenceDraft | null>(null);
  const create = useCreateGeofence();
  const update = useUpdateGeofence();
  const remove = useDeleteGeofence();
  // Géométrie modifiée via les poignées, en attente d'« Enregistrer la géométrie »
  const [editDraft, setEditDraft] = useState<{ id: string; draft: GeofenceDraft } | null>(null);
  const [editRevision, setEditRevision] = useState(0);
  const [renaming, setRenaming] = useState<Geofence | null>(null);
  const [deleting, setDeleting] = useState<Geofence | null>(null);
  // Modal règle d'alerte : création sur la zone sélectionnée ou édition d'une règle existante
  const [ruleEditor, setRuleEditor] = useState<{ geofence: Geofence; rule: GeofenceRule | null } | null>(null);

  const stopDrawing = () => {
    setDrawMode(null);
    setDraft(null);
  };

  // Échap annule le dessin en cours (avant la saisie du nom)
  useEffect(() => {
    if (!drawMode || draft) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && stopDrawing();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawMode, draft]);

  const handleDrawn = (d: GeofenceDraft) => {
    const error = draftError(d);
    if (error) {
      toast.error(error);
      stopDrawing();
      return;
    }
    setDraft(d);
  };

  const startDrawing = (mode: DrawMode) => {
    setSelectedId(null);
    setDrawMode(mode);
  };

  /** Changer de zone abandonnerait une géométrie non enregistrée : refusé tant qu'elle est en attente. */
  const guardDirty = () => {
    if (!editDraft) return false;
    toast.info('Enregistrez ou annulez la modification de la géométrie en cours');
    return true;
  };

  // Liste → sélection + recadrage ; carte → sélection seule
  const pickFromList = (id: string) => {
    if (id !== selectedId && guardDirty()) return;
    setSelectedId(id);
    setFocusRequest((n) => n + 1);
  };
  const pickFromMap = (id: string | null) => {
    if (id !== selectedId && guardDirty()) return;
    setSelectedId(id);
  };

  const cancelEdit = () => {
    setEditDraft(null);
    setEditRevision((n) => n + 1);
  };

  /** Zone supprimée ailleurs entre-temps : message + liste relue (la mutation invalide la requête). */
  const handleGone = (err: unknown, fallback: string) => {
    toastApiError(err, fallback);
    if (apiErrorCode(err) === 'NOT_FOUND') {
      setSelectedId(null);
      setEditDraft(null);
    }
  };

  const saveGeometry = () => {
    if (!editDraft) return;
    const error = draftError(editDraft.draft);
    if (error) {
      toast.error(error);
      return;
    }
    update.mutate({ id: editDraft.id, ...editDraft.draft }, {
      onSuccess: () => {
        toast.success('Géométrie enregistrée');
        setEditDraft(null);
      },
      onError: (err) => handleGone(err, 'Enregistrement impossible'),
    });
  };

  const confirmDelete = () => {
    if (!deleting) return;
    const { id, name } = deleting;
    remove.mutate(id, {
      onSuccess: () => toast.success(`Zone « ${name} » supprimée`),
      onError: (err) => handleGone(err, 'Suppression impossible'),
      onSettled: () => {
        setDeleting(null);
        if (selectedId === id) {
          setSelectedId(null);
          setEditDraft(null);
        }
      },
    });
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col md:flex-row">{/* 3.5rem = TopBar */}
      <aside className="md:w-80 shrink-0 max-h-[40vh] md:max-h-none overflow-y-auto bg-card border-b md:border-b-0 md:border-r p-3 space-y-3">
        <h1 className="font-semibold">Géofences {query.data && <span className="text-muted-foreground">({geofences.length})</span>}</h1>
        <QueryError query={query} className="flex-wrap" />
        <GeofenceList
          geofences={geofences}
          isLoading={query.isLoading}
          selectedId={selectedId}
          onPick={pickFromList}
          renderSelected={(g) => (
            <>
              <dl className="text-xs text-muted-foreground space-y-0.5">
                <div>Catégorie : {CATEGORY_LABELS[g.category] ?? g.category}</div>
                {g.areaKm2 != null && <div>Surface : {fmtArea(g.areaKm2)}</div>}
                {g.description && <div className="whitespace-pre-line">{g.description}</div>}
                {canWrite && (
                  <>
                    <p className="pt-1">
                      {g.metadata?.shape === 'circle'
                        ? 'Déplacez la zone ou sa poignée sur la carte pour la modifier.'
                        : 'Déplacez la zone ou ses points sur la carte pour la modifier.'}
                    </p>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" variant="outline" onClick={() => setRenaming(g)}>Renommer</Button>
                      <Button size="sm" variant="outline" className="text-danger" onClick={() => setDeleting(g)}>Supprimer</Button>
                    </div>
                  </>
                )}
              </dl>
              <GeofenceRulesPanel
                geofenceId={g.id}
                canWrite={canWrite}
                onCreate={() => setRuleEditor({ geofence: g, rule: null })}
                onEdit={(rule) => setRuleEditor({ geofence: g, rule })}
              />
            </>
          )}
        />
      </aside>

      <div className="relative flex-1 min-h-[50vh]">
        <GeofenceMap
          geofences={geofences}
          selectedId={selectedId}
          onSelect={pickFromMap}
          focusRequest={focusRequest}
          drawMode={drawMode}
          onDrawn={handleDrawn}
          editable={canWrite}
          editRevision={editRevision}
          onEdit={(id, d) => setEditDraft({ id, draft: d })}
        />
        {canWrite && (
          <div className="absolute left-3 top-3 right-14">
            {editDraft ? (
              <div role="toolbar" aria-label="Modification en cours" className="flex flex-wrap items-center gap-2 rounded-md bg-card/95 border shadow p-2">
                <span className="text-sm">Géométrie modifiée</span>
                <Button size="sm" disabled={update.isPending} onClick={saveGeometry}>
                  {update.isPending ? 'Enregistrement…' : 'Enregistrer la géométrie'}
                </Button>
                <Button size="sm" variant="outline" disabled={update.isPending} onClick={cancelEdit}>Annuler</Button>
              </div>
            ) : (
              <DrawToolbar drawMode={drawMode} disabled={!query.isSuccess} onStart={startDrawing} onCancel={stopDrawing} />
            )}
          </div>
        )}
        {query.isSuccess && geofences.length === 0 && !drawMode && (
          <div className="pointer-events-none absolute inset-x-0 bottom-8 flex justify-center px-4">
            <p className="rounded-md bg-card/95 border shadow px-4 py-2 text-sm">Aucune zone définie, dessinez-en une</p>
          </div>
        )}
      </div>

      <GeofenceNameDialog
        open={draft != null}
        title="Enregistrer la zone"
        description={draft?.metadata.shape === 'circle' ? 'Nouvelle zone circulaire' : 'Nouvelle zone polygonale'}
        submitLabel="Enregistrer"
        pending={create.isPending}
        onSubmit={async ({ name, description }) => {
          const created = await create.mutateAsync({ ...draft!, name, ...(description ? { description } : {}) });
          toast.success(`Zone « ${created.name} » créée`);
          stopDrawing();
          setSelectedId(created.id);
        }}
        onError={(err) => {
          // Géométrie refusée par l'API : non corrigeable dans le formulaire, le dessin est abandonné
          toastApiError(err, 'Création impossible');
          stopDrawing();
        }}
        onCancel={stopDrawing}
      />

      <GeofenceNameDialog
        open={renaming != null}
        title="Modifier la zone"
        submitLabel="Enregistrer"
        initial={{ name: renaming?.name, description: renaming?.description ?? '' }}
        pending={update.isPending}
        onSubmit={async ({ name, description }) => {
          await update.mutateAsync({ id: renaming!.id, name, description: description || null });
          toast.success('Zone mise à jour');
          setRenaming(null);
        }}
        onError={(err) => {
          handleGone(err, 'Modification impossible');
          setRenaming(null);
        }}
        onCancel={() => setRenaming(null)}
      />

      <DeleteGeofenceDialog
        name={deleting?.name ?? null}
        pending={remove.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />

      <GeofenceRuleDialog
        geofence={ruleEditor?.geofence ?? null}
        rule={ruleEditor?.rule ?? null}
        onClose={() => setRuleEditor(null)}
      />
    </div>
  );
}
